import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { PRICE_SCHEDULE_MODES } from "../../common_nextgen/utils/Constants";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import useLookupNavigation from "../../common_nextgen/common_components/common-hooks/useLookupNavigation";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";

import {
  formFields,
  screenInfo,
} from "./AdditionalPatientPayScheduleFormConfig";
import { ROUTE_PATHS } from "../../common_nextgen/utils/Constants";

const AdditionalPatientPayScheduleForm = ({
  mode,
  intialSchedule,
  initialSelectedPatient,
  selectMode,
  hideModal,
}) => {
  const { PATIENT_PAY_SCHEDULE_CRITERIA } = ROUTE_PATHS;
  const location = useLocation();
  const { axios_helper_no_header } = useAxios();
  const { state: stateVal } = location;
  let [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);

  let {
    selectedAdditionalPatients = [],
    previousScreen = PATIENT_PAY_SCHEDULE_CRITERIA,
    currentIndex: initialIndex = 0,
    paramsData,
    ps3PatientPaySchName,
    lookupMode: initialLookupMode = false,
    formData: baseFormData = {},
    lookupScreen,
    lookupParams,
    navigateData,
  } = currentState;
  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState(baseFormData);
  const [dataForSubmission, setDataForSubmission] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  const handleBack = () => {
    if (selectMode) {
      hideModal();
    } else if (currentIndex < selectedAdditionalPatients.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      const lastScheduleName =
        selectedAdditionalPatients[currentIndex]?.apsAddtlPatPaySch || "";
      const updatedParamsData = {
        ...currentState.paramsData,
        apsAddtlPatPaySch: lastScheduleName,
        hiddenRecordIndicator: "Active",
      };
      const lastRecord = previousState.pop();
      navigateToPage(previousScreen, [
        ...previousState,
        {
          ...lastRecord,
          paramsData: updatedParamsData,
          currentIndex,
        },
      ]);
    }
  };

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/apsAdlPatPaySched";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/apsAdlPatPaySched";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        currentState = {
          ...currentState,
          selectedAdditionalPatients: [{ ...responseData }],
          currentIndex: 0,
        };
        navigateToPage(ROUTE_PATHS.ADDITIONAL_PATIENT_PAY_SCHEDULE_EDIT, [
          ...previousState,
          currentState,
        ]);
      } else {
        setInitialFormData(responseData);
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};
    if (selectMode) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";
      keyFunc.F7 = () => {
        navigateToPage(ROUTE_PATHS.ADDITIONAL_PATIENT_PAY_STEPS, [
          ...previousState,
          { ...currentState, currentIndex },
          {
            selectedSchedules: [initialFormData],
            previousScreen: location.pathname,
          },
        ]);
      };
      keyName.F7 = "Steps";
      keyFunc.F14 = () => {
        navigateToPage(ROUTE_PATHS.ADDITIONAL_PATIENT_PAY_NOTE, [
          ...previousState,
          { ...currentState, currentIndex },
          {
            selectedAdditionalPatients: [initialFormData],
            previousScreen: location.pathname,
          },
        ]);
      };

      keyName.F14 = "Note";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";
      keyFunc.F7 = () => {
        navigateToPage(ROUTE_PATHS.ADDITIONAL_PATIENT_PAY_STEPS, [
          ...previousState,
          { ...currentState, currentIndex },
          {
            selectedSchedules: [initialFormData],
            previousScreen: location.pathname,
          },
        ]);
      };
      keyName.F7 = "Steps";
      keyFunc.F14 = () => {
        navigateToPage(ROUTE_PATHS.ADDITIONAL_PATIENT_PAY_NOTE, [
          ...previousState,
          { ...currentState, currentIndex },
          {
            selectedAdditionalPatients: [initialFormData],
            previousScreen: location.pathname,
          },
        ]);
      };
      keyName.F14 = "Note";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, initialFormData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const url = isAddMode(currentMode)
        ? `api/apsAdlPatPaySched/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`
        : `api/apsAdlPatPaySched/${encodeURIComponent(
            selectMode
              ? initialSelectedPatient[0]?.apsAddtlPatPaySch
              : selectedAdditionalPatients[currentIndex]?.apsAddtlPatPaySch
          )}`;
      const { responseData } = await axios_helper_no_header("GET", url);
      setInitialFormData(responseData);
    };

    fetchInitialData();
  }, [mode, currentIndex, selectMode]);

  const { lookupData, fetchLookupData, viewLookupDetails } =
    useLookupNavigation({
      initialLookupMode,
      lookupParams,
      initialIndex,
      previousState,
      currentState,
      lookupScreen,
      navigateData,
      initialFormData,
    });

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
          lookupData={lookupData}
          fetchLookupData={fetchLookupData}
          viewLookupDetails={viewLookupDetails}
        />
      </div>
    </div>
  );
};

export default AdditionalPatientPayScheduleForm;
